/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, useCallback, useEffect, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import DecisionIcon from '@atlaskit/icon/core/decision';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';

import { paramsToObject } from '../utils/params-to-object';

import TokenWizardModalBody from './components/modal-body';

/**
 * __Token wizard modal__
 *
 * A token wizard modal on the tokens reference list page.
 *
 */
const TokenWizardModal = (): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const [params, setParams] = useState({});
	const openModal = useCallback(
		(_e: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			setIsOpen(true);
			fireTrackAnalytics(analyticsEvent, 'button clicked', 'openTokenPicker', {
				source: 'token picker button',
			});
		},
		[],
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		try {
			const queryString = window.location.hash
				? window?.location?.hash?.split('?')[1]
				: window?.location?.search;
			const urlParams = new URLSearchParams(queryString);
			const entries = urlParams.entries();
			const paramInURL = paramsToObject(entries);
			setParams(paramInURL);
			if (!!paramInURL.isTokenPickerOpen) {
				setIsOpen(true);
				const analyticsEvent = createAnalyticsEvent({});
				fireTrackAnalytics(analyticsEvent, 'button clicked', 'openTokenPicker', {
					source: 'url',
				});
			}
		} catch {
			// eslint-disable-next-line no-console
			console.error('Invalid query parameters in URL');
		}
	}, [createAnalyticsEvent]);

	const removeParamsInUrl = useCallback(() => {
		window?.history?.pushState(
			{},
			document?.title,
			`${window?.location.pathname}${
				window?.location.hash ? window?.location.hash.split('?')[0] : ''
			}`,
		);
	}, []);

	const closeModal = () => {
		if (Object.keys(params).length) {
			setParams({});
			removeParamsInUrl();
		}
		setIsOpen(false);
	};

	return (
		<Fragment>
			<Button onClick={openModal} iconBefore={DecisionIcon}>
				Token picker
			</Button>

			<ModalTransition>
				{isOpen && (
					<Modal onClose={closeModal} width="x-large" height={640}>
						<ModalHeader hasCloseButton>
							<ModalTitle>Token picker</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<TokenWizardModalBody params={params} removeParamsInUrl={removeParamsInUrl} />
						</ModalBody>
					</Modal>
				)}
			</ModalTransition>
		</Fragment>
	);
};

export default TokenWizardModal;
